import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { buildEmptyItemsArray, usePaginationConfig } from '@yme/atoms/pagination';
import { Filters } from '@yme/common/types';
import { useWindowSize } from '@yme/hooks/useWindowSize';
import { ManualKeyValues } from './constants';
import { convertActiveItemsToNull, convertFiltersToEmptyArr, filterManualsArray, generateUniqueManuals, getEncodedLocationSearch, getFilterData, getManualPerPage, setDefaultActiveItems, sortFiltersByLabel, } from './helpers';
export function useOwnerManualsLogic({ data, categories, placeholders, }) {
    const [manuals, setManuals] = useState([]);
    const [manualsPerPage, setManualsPerPage] = useState([]);
    const [existedManuals, setExistedManuals] = useState({
        categoryManuals: [],
        segmentManuals: [],
        modelManuals: [],
        yearManuals: [],
        languageManuals: [],
    });
    const [activeItems, setActiveItems] = useState({
        category: null,
        segment: null,
        model: null,
        year: null,
        language: null,
    });
    const [filtersData, setFiltersData] = useState({
        categoryData: [],
        segmentData: [],
        modelData: [],
        yearData: [],
        languageData: [],
    });
    const { categoryPlaceholder, segmentPlaceholder, modelPlaceholder, yearPlaceholder, languagePlaceholder, } = placeholders;
    const history = useHistory();
    const { isMobileView } = useWindowSize();
    const [config, setConfig] = useState({
        page: 1,
        perPage: 20,
        total: 0,
        isPageValueReset: false,
    });
    const paginationConfigData = {
        currentPage: config.page,
        activeElement: config.perPage,
        countItems: Math.floor(config.total / config.perPage),
        items: buildEmptyItemsArray(Math.ceil(manuals.length ? config.total / config.perPage : 1)),
        isPageValueReset: config.isPageValueReset,
    };
    const { paginationConfig, activeItemValue, onClickItemHandler, currentPerPageValue, setCurrentPerPageValue, } = usePaginationConfig({
        paginationData: paginationConfigData,
        isMobileView,
    });
    const pageValueResetUpdate = useCallback(() => {
        setConfig((prevState) => {
            return {
                ...prevState,
                isPageValueReset: false,
            };
        });
    }, []);
    const onFocusFilterHandler = useCallback(() => {
        const queryParams = new URLSearchParams(window.location.search);
        if (queryParams.size) {
            history.push({
                query: '',
            });
        }
    }, [history]);
    const categoryFilterOnChange = useCallback((value) => {
        pageValueResetUpdate();
        setActiveItems({
            category: value === categoryPlaceholder ? null : value,
            ...convertActiveItemsToNull(['segment', 'model', 'year', 'language']),
        });
        setFiltersData((prevData) => ({
            ...prevData,
            ...convertFiltersToEmptyArr(['segmentData', 'modelData', 'yearData', 'languageData']),
        }));
    }, [categoryPlaceholder, pageValueResetUpdate]);
    const segmentFilterOnChange = useCallback((value) => {
        pageValueResetUpdate();
        const segmentManuals = filterManualsArray(existedManuals.categoryManuals, ManualKeyValues.SEGMENT, value);
        setActiveItems((prevData) => ({
            ...prevData,
            segment: value === segmentPlaceholder ? null : value,
            ...convertActiveItemsToNull(['model', 'year', 'language']),
        }));
        setFiltersData((prevData) => ({
            ...prevData,
            modelData: sortFiltersByLabel(getFilterData(segmentManuals, ManualKeyValues.MODEL)),
            ...convertFiltersToEmptyArr(['yearData', 'languageData']),
        }));
        setExistedManuals((prevState) => {
            return {
                ...prevState,
                segmentManuals,
            };
        });
        setManuals(segmentManuals);
    }, [existedManuals.categoryManuals, pageValueResetUpdate, segmentPlaceholder]);
    const modelFilterOnChange = useCallback((value) => {
        pageValueResetUpdate();
        const modelManuals = filterManualsArray(existedManuals.segmentManuals, ManualKeyValues.MODEL, value);
        setActiveItems((prevData) => ({
            ...prevData,
            model: value === modelPlaceholder ? null : value,
            ...convertActiveItemsToNull(['year', 'language']),
        }));
        setFiltersData((prevData) => ({
            ...prevData,
            yearData: sortFiltersByLabel(getFilterData(modelManuals, ManualKeyValues.YEAR), false),
            languageData: [],
        }));
        setExistedManuals((prevState) => {
            return {
                ...prevState,
                modelManuals,
            };
        });
        setManuals(modelManuals);
    }, [existedManuals.segmentManuals, modelPlaceholder, pageValueResetUpdate]);
    const yearFilterOnChange = useCallback((value) => {
        pageValueResetUpdate();
        const yearManuals = filterManualsArray(existedManuals.modelManuals, ManualKeyValues.YEAR, value);
        setActiveItems((prevData) => ({
            ...prevData,
            year: value === yearPlaceholder ? null : value,
            language: null,
        }));
        setFiltersData((prevData) => ({
            ...prevData,
            languageData: sortFiltersByLabel(getFilterData(yearManuals, ManualKeyValues.LANGUAGE)),
        }));
        setExistedManuals((prevState) => {
            return {
                ...prevState,
                yearManuals,
            };
        });
        setManuals(yearManuals);
    }, [existedManuals.modelManuals, pageValueResetUpdate, yearPlaceholder]);
    const languageFilterOnChange = useCallback((value) => {
        pageValueResetUpdate();
        setActiveItems((prevData) => ({
            ...prevData,
            language: value === languagePlaceholder ? null : value,
        }));
        setManuals(filterManualsArray(existedManuals.yearManuals, ManualKeyValues.LANGUAGE, value));
    }, [existedManuals.yearManuals, languagePlaceholder, pageValueResetUpdate]);
    useEffect(() => {
        const { category, segment, model, year, language } = activeItems;
        const { categoryData, segmentData, modelData, yearData, languageData } = filtersData;
        const encodedLocationSearch = getEncodedLocationSearch(window.location.search);
        const queryParams = new URLSearchParams(encodedLocationSearch);
        const categoryQueryValue = queryParams.get(Filters.CATEGORY);
        const segmentQueryValue = queryParams.get(Filters.SEGMENT);
        const modelQueryValue = queryParams.get(Filters.MODEL);
        const yearQueryValue = queryParams.get(Filters.YEAR);
        const languageQueryValue = queryParams.get(Filters.LANGUAGE);
        if (categoryQueryValue && !category && categoryData.length) {
            setDefaultActiveItems(categoryData, categoryQueryValue, categoryFilterOnChange);
        }
        if (segmentQueryValue && !segment && category && segmentData.length) {
            setDefaultActiveItems(segmentData, segmentQueryValue, segmentFilterOnChange);
        }
        if (modelQueryValue && !model && segment && modelData.length) {
            setDefaultActiveItems(modelData, modelQueryValue, modelFilterOnChange);
        }
        if (yearQueryValue && !year && model && yearData.length) {
            setDefaultActiveItems(yearData, yearQueryValue, yearFilterOnChange);
        }
        if (languageQueryValue && !language && year && languageData.length) {
            setDefaultActiveItems(languageData, languageQueryValue, languageFilterOnChange);
        }
    }, [
        activeItems,
        filtersData,
        categoryFilterOnChange,
        modelFilterOnChange,
        segmentFilterOnChange,
        yearFilterOnChange,
        languageFilterOnChange,
    ]);
    useEffect(() => {
        if (categories.length) {
            const categoriesFilterData = categories.map((category) => {
                return {
                    value: category,
                    label: category,
                };
            });
            setFiltersData((prevData) => ({
                ...prevData,
                categoryData: sortFiltersByLabel(categoriesFilterData),
            }));
        }
    }, [categories]);
    useEffect(() => {
        if (manuals?.length) {
            setConfig((prevState) => {
                return {
                    ...prevState,
                    total: manuals.length,
                    isPageValueReset: true,
                };
            });
        }
    }, [manuals]);
    useEffect(() => {
        if (manuals.length && config.total) {
            const currentManuals = getManualPerPage(manuals, activeItemValue, currentPerPageValue);
            setManualsPerPage(currentManuals);
        }
    }, [activeItemValue, config.total, currentPerPageValue, manuals]);
    useEffect(() => {
        if (data.length) {
            const uniqueManuals = generateUniqueManuals(data);
            setExistedManuals((prevState) => {
                return {
                    ...prevState,
                    categoryManuals: uniqueManuals,
                };
            });
            setManuals(uniqueManuals);
            setFiltersData((prevData) => ({
                ...prevData,
                segmentData: sortFiltersByLabel(getFilterData(uniqueManuals, ManualKeyValues.SEGMENT)),
                ...convertFiltersToEmptyArr(['modelData', 'yearData', 'languageData']),
            }));
        }
    }, [data]);
    return {
        categoryFilterOnChange,
        segmentFilterOnChange,
        modelFilterOnChange,
        yearFilterOnChange,
        languageFilterOnChange,
        onFocusFilterHandler,
        activeItems,
        filtersData,
        manuals: manualsPerPage,
        //pagination params
        config,
        paginationConfigData,
        paginationConfig,
        activeItemValue,
        onClickItemHandler,
        setCurrentPerPageValue,
    };
}
